import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

console.log('GSAP loaded:', gsap);
console.log('ScrollTrigger loaded:', ScrollTrigger);

// ========================================
// PREMIUM ART SHOWCASE ANIMATIONS
// ========================================

// Simple test animation to verify GSAP is working
export const testAnimation = (elementRef) => {
  if (!elementRef.current) {
    console.log('testAnimation: elementRef.current is null');
    return;
  }

  console.log('testAnimation: Starting test animation');
  
  gsap.fromTo(elementRef.current, 
    {
      opacity: 0,
      scale: 0.5,
      rotation: -180
    },
    {
      opacity: 1,
      scale: 1,
      rotation: 0,
      duration: 2,
      ease: "bounce.out"
    }
  );
};

// 1. ENHANCED SCROLL REVEAL - Multiple Animation Types
export const scrollRevealAnimation = (elementRef, options = {}) => {
  if (!elementRef.current) {
    console.log('scrollRevealAnimation: elementRef.current is null');
    return;
  }

  const {
    direction = 'up',
    distance = 50,
    duration = 1,
    delay = 0,
    scale = 1,
    rotation = 0,
    stagger = 0
  } = options;

  console.log('scrollRevealAnimation: Starting animation for', elementRef.current);

  const startY = direction === 'up' ? distance : direction === 'down' ? -distance : 0;
  const startX = direction === 'left' ? -distance : direction === 'right' ? distance : 0;

  const elements = elementRef.current.children ? Array.from(elementRef.current.children) : [elementRef.current];

  gsap.set(elements, { 
    opacity: 0, 
    y: startY,
    x: startX,
    scale: scale === 1 ? 1 : 0.8,
    rotation: rotation
  });

  gsap.to(elements, {
    opacity: 1,
    y: 0,
    x: 0,
    scale: 1,
    rotation: 0,
    duration: duration,
    ease: "back.out(1.7)",
    stagger: stagger,
    delay: delay,
    scrollTrigger: {
      trigger: elementRef.current,
      start: "top 85%",
      toggleActions: "play none none reverse",
      onEnter: () => console.log('ScrollTrigger activated for', elementRef.current)
    }
  });
};

// 2. SUBTLE PARALLAX EFFECT for Hero Images
export const parallaxAnimation = (elementRef, speed = 0.5) => {
  if (!elementRef.current) return;

  gsap.to(elementRef.current, {
    yPercent: -50 * speed,
    ease: "none",
    scrollTrigger: {
      trigger: elementRef.current,
      start: "top bottom",
      end: "bottom top",
      scrub: true
    }
  });
};

// 3. 3D CARD FLIP/HOVER for Gallery Items
export const card3DEffect = (cardRef) => {
  if (!cardRef.current) return;

  const card = cardRef.current;
  
  const handleMouseEnter = (e) => {
    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    
    const rotateX = (mouseY / rect.height) * -10;
    const rotateY = (mouseX / rect.width) * 10;
    
    gsap.to(card, {
      rotationX: rotateX,
      rotationY: rotateY,
      scale: 1.05,
      duration: 0.6,
      ease: "power2.out",
      transformPerspective: 1000
    });
  };

  const handleMouseLeave = () => {
    gsap.to(card, {
      rotationX: 0,
      rotationY: 0,
      scale: 1,
      duration: 0.6,
      ease: "power2.out"
    });
  };

  card.addEventListener('mouseenter', handleMouseEnter);
  card.addEventListener('mouseleave', handleMouseLeave);

  return () => {
    card.removeEventListener('mouseenter', handleMouseEnter);
    card.removeEventListener('mouseleave', handleMouseLeave);
  };
};

// 4. ENHANCED KINETIC TYPOGRAPHY for Headlines
export const kineticTypography = (textRef, animationType = 'enhancedSplit') => {
  if (!textRef.current) {
    console.log('kineticTypography: textRef.current is null');
    return;
  }

  const text = textRef.current;
  console.log('kineticTypography: Starting animation for', text.textContent);
  
  if (animationType === 'enhancedSplit') {
    const words = text.textContent.split(' ');
    text.innerHTML = words.map((word, index) => 
      `<span class="word-${index}" style="display: inline-block; opacity: 0; transform: translateY(50px) scale(0.8);">${word}</span>`
    ).join(' ');
    
    // Enhanced animation with scale, rotation, and glow effects
    words.forEach((word, index) => {
      const wordElement = text.querySelector(`.word-${index}`);
      if (wordElement) {
        gsap.fromTo(wordElement,
          {
            opacity: 0,
            y: 50,
            scale: 0.8,
            rotationX: -90,
            filter: 'blur(10px)'
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotationX: 0,
            filter: 'blur(0px)',
            duration: 1.2,
            ease: "back.out(1.7)",
            delay: 0.8 + (index * 0.3),
            onComplete: () => {
              // Add subtle floating animation
              gsap.to(wordElement, {
                y: -5,
                duration: 2,
                yoyo: true,
                repeat: -1,
                ease: "power2.inOut",
                delay: index * 0.1
              });
            }
          }
        );
      }
    });
  } else if (animationType === 'splitWords') {
    const words = text.textContent.split(' ');
    text.innerHTML = words.map(word => `<span class="word" style="display: inline-block; opacity: 0;">${word}</span>`).join(' ');
    
    gsap.fromTo(text.querySelectorAll('.word'),
      {
        opacity: 0,
        y: 30
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.2,
        ease: "power2.out",
        delay: 0.5
      }
    );
  } else if (animationType === 'typewriter') {
    const originalText = text.textContent;
    text.textContent = '';
    
    gsap.to(text, {
      duration: 2,
      ease: "none",
      onUpdate: function() {
        const progress = this.progress();
        const currentLength = Math.round(originalText.length * progress);
        text.textContent = originalText.substring(0, currentLength);
      },
      delay: 1
    });
  }
};

// 4.5. PREMIUM TEXT GLOW EFFECT for Main Title
export const textGlowEffect = (textRef) => {
  if (!textRef.current) {
    console.log('textGlowEffect: textRef.current is null');
    return;
  }

  const text = textRef.current;
  console.log('textGlowEffect: Starting glow animation for', text.textContent);

  // Add initial glow and pulsing effect
  gsap.set(text, {
    textShadow: '0 0 20px rgba(255, 215, 0, 0.8), 0 0 40px rgba(255, 165, 0, 0.6), 0 0 60px rgba(255, 69, 0, 0.4)',
    filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.7))'
  });

  // Pulsing glow animation
  gsap.to(text, {
    textShadow: '0 0 30px rgba(255, 215, 0, 1), 0 0 60px rgba(255, 165, 0, 0.8), 0 0 90px rgba(255, 69, 0, 0.6)',
    filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 1))',
    duration: 2,
    yoyo: true,
    repeat: -1,
    ease: "power2.inOut"
  });

  // Subtle scale breathing effect
  gsap.to(text, {
    scale: 1.02,
    duration: 3,
    yoyo: true,
    repeat: -1,
    ease: "power1.inOut"
  });
};

// 5. SCROLL-TRIGGERED GALLERY GRID ANIMATION
export const galleryGridAnimation = (gridRef) => {
  if (!gridRef.current) {
    console.log('galleryGridAnimation: gridRef.current is null');
    return;
  }

  const items = gridRef.current.querySelectorAll('.gallery-item');
  if (items.length === 0) return;

  console.log('galleryGridAnimation: Animating', items.length, 'gallery items');

  gsap.set(items, {
    opacity: 0,
    scale: 0.8,
    y: 60,
    rotation: -10
  });

  gsap.to(items, {
    opacity: 1,
    scale: 1,
    y: 0,
    rotation: 0,
    duration: 0.8,
    ease: "back.out(1.7)",
    stagger: {
      amount: 1.2,
      from: "center",
      grid: "auto"
    },
    scrollTrigger: {
      trigger: gridRef.current,
      start: "top 80%",
      toggleActions: "play none none reverse"
    }
  });
};

// 6. SECTION TITLE ANIMATION WITH UNDERLINE
export const sectionTitleAnimation = (titleRef) => {
  if (!titleRef.current) {
    console.log('sectionTitleAnimation: titleRef.current is null');
    return;
  }

  console.log('sectionTitleAnimation: Starting animation for', titleRef.current);

  // Create underline element
  const underline = document.createElement('div');
  underline.className = 'section-underline';
  underline.style.cssText = `
    height: 3px;
    background: linear-gradient(90deg, #f59e0b, #ef4444);
    margin: 10px auto;
    border-radius: 2px;
  `;
  titleRef.current.parentNode.insertBefore(underline, titleRef.current.nextSibling);

  gsap.set([titleRef.current, underline], {
    opacity: 0,
    y: 30
  });

  gsap.set(underline, { width: 0 });

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: titleRef.current,
      start: "top 85%",
      toggleActions: "play none none reverse"
    }
  });

  tl.to(titleRef.current, {
    opacity: 1,
    y: 0,
    duration: 0.8,
    ease: "power2.out"
  })
  .to(underline, {
    opacity: 1,
    y: 0,
    width: "80px",
    duration: 0.6,
    ease: "power2.out"
  }, "-=0.3");
};

// 7. SCROLL-BASED COUNTER ANIMATION
export const counterAnimation = (counterRef, targetNumber, duration = 2) => {
  if (!counterRef.current) {
    console.log('counterAnimation: counterRef.current is null');
    return;
  }

  console.log('counterAnimation: Starting counter for', targetNumber);

  const counter = { value: 0 };
  
  gsap.to(counter, {
    value: targetNumber,
    duration: duration,
    ease: "power2.out",
    scrollTrigger: {
      trigger: counterRef.current,
      start: "top 80%",
      toggleActions: "play none none none"
    },
    onUpdate: () => {
      counterRef.current.textContent = Math.round(counter.value);
    }
  });
};

// 8. PARALLAX BACKGROUND SCROLL
export const parallaxBackgroundScroll = (elementRef, speed = 0.5) => {
  if (!elementRef.current) return;

  console.log('parallaxBackgroundScroll: Starting parallax for', elementRef.current);

  gsap.to(elementRef.current, {
    yPercent: -50 * speed,
    ease: "none",
    scrollTrigger: {
      trigger: elementRef.current,
      start: "top bottom",
      end: "bottom top",
      scrub: 1
    }
  });
};

// 9. STAGGERED CARD REVEAL
export const staggeredCardReveal = (containerRef) => {
  if (!containerRef.current) {
    console.log('staggeredCardReveal: containerRef.current is null');
    return;
  }

  const cards = containerRef.current.querySelectorAll('.card, .award-card, .image-card');
  if (cards.length === 0) return;

  console.log('staggeredCardReveal: Animating', cards.length, 'cards');

  gsap.set(cards, {
    opacity: 0,
    y: 80,
    scale: 0.9,
    rotationY: -15
  });

  gsap.to(cards, {
    opacity: 1,
    y: 0,
    scale: 1,
    rotationY: 0,
    duration: 1,
    ease: "power2.out",
    stagger: 0.2,
    scrollTrigger: {
      trigger: containerRef.current,
      start: "top 75%",
      toggleActions: "play none none reverse"
    }
  });
};

// 10. IMAGE HOVER ZOOM for Sculpture Details
export const imageHoverZoom = (imageRef, zoomLevel = 1.1) => {
  if (!imageRef.current) return;

  const image = imageRef.current;
  
  const handleMouseEnter = () => {
    gsap.to(image, {
      scale: zoomLevel,
      duration: 0.8,
      ease: "power2.out"
    });
  };

  const handleMouseLeave = () => {
    gsap.to(image, {
      scale: 1,
      duration: 0.8,
      ease: "power2.out"
    });
  };

  image.addEventListener('mouseenter', handleMouseEnter);
  image.addEventListener('mouseleave', handleMouseLeave);

  return () => {
    image.removeEventListener('mouseenter', handleMouseEnter);
    image.removeEventListener('mouseleave', handleMouseLeave);
  };
};

// Hero Slider Animation
export const animateHeroSlider = (slideRef, direction = 'next') => {
  if (!slideRef.current) return;

  const tl = gsap.timeline();
  
  if (direction === 'next') {
    tl.fromTo(slideRef.current,
      { x: '100%', opacity: 0 },
      { x: '0%', opacity: 1, duration: 1, ease: "power2.out" }
    );
  } else {
    tl.fromTo(slideRef.current,
      { x: '-100%', opacity: 0 },
      { x: '0%', opacity: 1, duration: 1, ease: "power2.out" }
    );
  }
};

// Text Reveal Animation
export const animateTextReveal = (textRef) => {
  if (!textRef.current) return;

  gsap.fromTo(textRef.current,
    {
      opacity: 0,
      y: 30,
    },
    {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: "power2.out",
      scrollTrigger: {
        trigger: textRef.current,
        start: "top 85%",
        toggleActions: "play none none reverse"
      }
    }
  );
};

// Card Hover Animation
export const cardHoverAnimation = (cardRef) => {
  if (!cardRef.current) return;

  const card = cardRef.current;
  const image = card.querySelector('img');
  const overlay = card.querySelector('.hover-overlay');

  gsap.set(overlay, { opacity: 0, y: 20 });

  const hoverIn = () => {
    gsap.to(image, { scale: 1.1, duration: 0.5, ease: "power2.out" });
    gsap.to(overlay, { opacity: 1, y: 0, duration: 0.3, ease: "power2.out" });
  };

  const hoverOut = () => {
    gsap.to(image, { scale: 1, duration: 0.5, ease: "power2.out" });
    gsap.to(overlay, { opacity: 0, y: 20, duration: 0.3, ease: "power2.out" });
  };

  card.addEventListener('mouseenter', hoverIn);
  card.addEventListener('mouseleave', hoverOut);

  // Cleanup function
  return () => {
    card.removeEventListener('mouseenter', hoverIn);
    card.removeEventListener('mouseleave', hoverOut);
  };
};

// Page Transition Animation
export const pageTransition = (elementRef) => {
  if (!elementRef.current) return;

  gsap.fromTo(elementRef.current,
    {
      opacity: 0,
      y: 50
    },
    {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out"
    }
  );
};

// Modal Animation
export const animateModal = (modalRef, isOpening = true) => {
  if (!modalRef.current) return;

  const modal = modalRef.current;
  const backdrop = modal.querySelector('.modal-backdrop');
  const content = modal.querySelector('.modal-content');

  if (isOpening) {
    gsap.set(modal, { display: 'flex' });
    gsap.fromTo(backdrop, 
      { opacity: 0 },
      { opacity: 1, duration: 0.3 }
    );
    gsap.fromTo(content,
      { opacity: 0, scale: 0.8, y: 50 },
      { opacity: 1, scale: 1, y: 0, duration: 0.4, ease: "back.out(1.7)" }
    );
  } else {
    gsap.to(backdrop, { opacity: 0, duration: 0.3 });
    gsap.to(content, {
      opacity: 0,
      scale: 0.8,
      y: 50,
      duration: 0.3,
      ease: "power2.in",
      onComplete: () => {
        gsap.set(modal, { display: 'none' });
      }
    });
  }
};

// Artist Profile Section Animation
export const animateArtistProfile = (containerRef) => {
  if (!containerRef.current) return;

  const image = containerRef.current.querySelector('.artist-image');
  const content = containerRef.current.querySelector('.artist-content');

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: containerRef.current,
      start: "top 70%",
      toggleActions: "play none none reverse"
    }
  });

  tl.fromTo(image,
    { opacity: 0, x: -50 },
    { opacity: 1, x: 0, duration: 1, ease: "power2.out" }
  )
  .fromTo(content,
    { opacity: 0, x: 50 },
    { opacity: 1, x: 0, duration: 1, ease: "power2.out" },
    "-=0.5"
  );
};

// Loading Animation
export const createLoadingAnimation = (loaderRef) => {
  if (!loaderRef.current) return;

  const tl = gsap.timeline({ repeat: -1 });
  
  tl.to(loaderRef.current, {
    rotation: 360,
    duration: 1,
    ease: "none"
  });

  return tl;
};

// ========================================
// NAVIGATION & UX MICRO-INTERACTIONS
// ========================================

// 1. ENHANCED MOBILE MENU ANIMATION with Stagger
export const mobileMenuAnimation = (menuRef, isOpen) => {
  if (!menuRef.current) return;

  const menuItems = menuRef.current.querySelectorAll('button');
  
  if (isOpen) {
    // Animate container
    gsap.fromTo(menuRef.current,
      {
        height: 0,
        opacity: 0,
        scale: 0.95
      },
      {
        height: 'auto',
        opacity: 1,
        scale: 1,
        duration: 0.4,
        ease: "back.out(1.7)"
      }
    );

    // Stagger animate menu items
    gsap.fromTo(menuItems,
      {
        x: -30,
        opacity: 0,
        scale: 0.9
      },
      {
        x: 0,
        opacity: 1,
        scale: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: "back.out(1.7)",
        delay: 0.2
      }
    );
  } else {
    // Animate out menu items first
    gsap.to(menuItems, {
      x: -20,
      opacity: 0,
      scale: 0.9,
      duration: 0.3,
      stagger: 0.05,
      ease: "power2.in"
    });

    // Then animate container
    gsap.to(menuRef.current, {
      height: 0,
      opacity: 0,
      scale: 0.95,
      duration: 0.4,
      ease: "power2.in",
      delay: 0.2
    });
  }
};

// 2. ICON FEEDBACK Micro-interactions
export const iconFeedback = (iconRef) => {
  if (!iconRef.current) return;

  const icon = iconRef.current;

  const handleMouseEnter = () => {
    gsap.to(icon, {
      scale: 1.1,
      color: "#a855f7", // Primary color
      duration: 0.3,
      ease: "power2.out"
    });
  };

  const handleMouseLeave = () => {
    gsap.to(icon, {
      scale: 1,
      color: "inherit",
      duration: 0.3,
      ease: "power2.out"
    });
  };

  icon.addEventListener('mouseenter', handleMouseEnter);
  icon.addEventListener('mouseleave', handleMouseLeave);

  return () => {
    icon.removeEventListener('mouseenter', handleMouseEnter);
    icon.removeEventListener('mouseleave', handleMouseLeave);
  };
};

// MASTER SCROLL ANIMATION INITIALIZER
export const initializeScrollAnimations = () => {
  console.log('Initializing comprehensive scroll animations...');
  
  // Smooth scroll behavior for the entire page
  gsap.registerPlugin(ScrollTrigger);
  
  // Global scroll settings
  ScrollTrigger.defaults({
    toggleActions: "play none none reverse",
    scroller: window
  });

  // Generic section animations
  const sections = document.querySelectorAll('section, .section');
  sections.forEach((section, index) => {
    // Skip the first section (right after slider) - it should be visible immediately
    if (index === 0) return;
    
    gsap.fromTo(section, 
      {
        opacity: 0,
        y: 50
      },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      }
    );
  });

  // Animate all images on scroll
  const images = document.querySelectorAll('img');
  images.forEach((img, index) => {
    // Skip images in footer or fixed containers
    const isInFooter = img.closest('footer');
    const isInFixedContainer = img.closest('.fixed, [style*="position: fixed"]');
    // Skip images in the first section (immediately visible after slider)
    const parentSection = img.closest('section');
    const allSections = document.querySelectorAll('section');
    const isInFirstSection = parentSection && parentSection === allSections[0];
    
    if (isInFooter || isInFixedContainer || isInFirstSection) return;
    
    gsap.fromTo(img,
      {
        opacity: 0,
        scale: 0.9,
        y: 30
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: img,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );
  });

  // Animate all buttons on scroll (excluding fixed/floating buttons)
  const buttons = document.querySelectorAll('button:not(.fixed button):not([class*="fixed"]), .btn, a[class*="bg-"]:not(.fixed a)');
  buttons.forEach((btn, index) => {
    // Skip buttons inside fixed containers or footer
    const isInFooter = btn.closest('footer');
    const isInFixedContainer = btn.closest('.fixed, [style*="position: fixed"]');
    // Skip buttons in the first section (immediately visible after slider)
    const parentSection = btn.closest('section');
    const allSections = document.querySelectorAll('section');
    const isInFirstSection = parentSection && parentSection === allSections[0];
    
    if (isInFooter || isInFixedContainer || isInFirstSection) return;
    
    gsap.fromTo(btn,
      {
        opacity: 0,
        scale: 0.8,
        y: 20
      },
      {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.6,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: btn,
          start: "top 90%",
          toggleActions: "play none none reverse"
        }
      }
    );
  });

  console.log('✅ Scroll animations initialized successfully!');
};

// 3. ENHANCED STICKY NAVIGATION with Parallax & Blur Effects
export const stickyNavigation = (navRef) => {
  if (!navRef.current) return;

  console.log('stickyNavigation: Initializing enhanced sticky navigation');

  // Initial navbar setup
  gsap.set(navRef.current, {
    background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(249,250,251,0.9) 100%)",
    backdropFilter: "blur(20px)",
    borderBottom: "1px solid rgba(229,231,235,0.5)"
  });

  ScrollTrigger.create({
    trigger: "body",
    start: "100px top",
    end: "bottom bottom",
    onEnter: () => {
      gsap.to(navRef.current, {
        background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(249,250,251,0.95) 100%)",
        backdropFilter: "blur(25px)",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 20px rgba(0, 0, 0, 0.1)",
        borderBottom: "1px solid rgba(251,146,60,0.2)",
        scale: 0.98,
        y: 0,
        duration: 0.6,
        ease: "power3.out"
      });
    },
    onLeaveBack: () => {
      gsap.to(navRef.current, {
        background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(249,250,251,0.9) 100%)",
        backdropFilter: "blur(20px)",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        borderBottom: "1px solid rgba(229,231,235,0.3)",
        scale: 1,
        duration: 0.6,
        ease: "power3.out"
      });
    },
    onUpdate: (self) => {
      // Parallax effect for navbar elements
      const progress = self.progress;
      const navItems = navRef.current.querySelectorAll('button, .logo-container');
      
      gsap.to(navItems, {
        y: progress * -2,
        duration: 0.3,
        ease: "power2.out"
      });
    }
  });

  // Add subtle floating animation to navbar
  gsap.to(navRef.current, {
    y: -1,
    duration: 2,
    yoyo: true,
    repeat: -1,
    ease: "power2.inOut"
  });
};

// ENHANCED NAV BUTTON RIPPLE EFFECT
export const navButtonRippleEffect = () => {
  console.log('navButtonRippleEffect: Initializing ripple effects for nav buttons');
  
  // Add ripple effect to all nav buttons
  const navButtons = document.querySelectorAll('nav button');
  
  navButtons.forEach(button => {
    button.addEventListener('click', function(e) {
      // Create ripple element
      const ripple = document.createElement('span');
      const rect = this.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;
      
      ripple.style.cssText = `
        position: absolute;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.6);
        transform: scale(0);
        animation: ripple 0.6s linear;
        left: ${x}px;
        top: ${y}px;
        width: ${size}px;
        height: ${size}px;
        pointer-events: none;
        z-index: 1;
      `;
      
      // Add CSS animation if not exists
      if (!document.querySelector('#ripple-styles')) {
        const style = document.createElement('style');
        style.id = 'ripple-styles';
        style.textContent = `
          @keyframes ripple {
            to {
              transform: scale(2);
              opacity: 0;
            }
          }
        `;
        document.head.appendChild(style);
      }
      
      this.style.position = 'relative';
      this.style.overflow = 'hidden';
      this.appendChild(ripple);
      
      // Remove ripple after animation
      setTimeout(() => {
        if (ripple.parentNode) {
          ripple.parentNode.removeChild(ripple);
        }
      }, 600);
    });
  });
};

// 4. SUB-MENU DROPDOWN Animation
export const subMenuDropdown = (submenuRef, isVisible) => {
  if (!submenuRef.current) return;

  if (isVisible) {
    gsap.fromTo(submenuRef.current,
      {
        opacity: 0,
        y: -10,
        scaleY: 0.8
      },
      {
        opacity: 1,
        y: 0,
        scaleY: 1,
        duration: 0.3,
        ease: "power2.out",
        transformOrigin: "top center"
      }
    );
  } else {
    gsap.to(submenuRef.current, {
      opacity: 0,
      y: -10,
      scaleY: 0.8,
      duration: 0.2,
      ease: "power2.in"
    });
  }
};

// 5. NAVIGATION LINK HOVER Effects
export const navLinkHover = (linkRef) => {
  if (!linkRef.current) return;

  const link = linkRef.current;
  
  // Create underline element
  const underline = document.createElement('div');
  underline.className = 'nav-underline';
  underline.style.cssText = `
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #a855f7, #ec4899);
    transform: scaleX(0);
    transform-origin: right;
  `;
  link.style.position = 'relative';
  link.appendChild(underline);

  const handleMouseEnter = () => {
    gsap.to(underline, {
      scaleX: 1,
      transformOrigin: 'left',
      duration: 0.4,
      ease: "power2.out"
    });
    
    gsap.to(link, {
      color: "#a855f7",
      duration: 0.3,
      ease: "power2.out"
    });
  };

  const handleMouseLeave = () => {
    gsap.to(underline, {
      scaleX: 0,
      transformOrigin: 'right',
      duration: 0.4,
      ease: "power2.out"
    });
    
    gsap.to(link, {
      color: "inherit",
      duration: 0.3,
      ease: "power2.out"
    });
  };

  link.addEventListener('mouseenter', handleMouseEnter);
  link.addEventListener('mouseleave', handleMouseLeave);

  return () => {
    link.removeEventListener('mouseenter', handleMouseEnter);
    link.removeEventListener('mouseleave', handleMouseLeave);
    if (underline.parentNode) {
      underline.parentNode.removeChild(underline);
    }
  };
};

// Contact Form Animation
export const animateContactForm = (formRef) => {
  if (!formRef.current) return;

  const fields = formRef.current.querySelectorAll('.form-field');
  
  gsap.fromTo(fields,
    {
      opacity: 0,
      y: 30
    },
    {
      opacity: 1,
      y: 0,
      duration: 0.6,
      stagger: 0.1,
      ease: "power2.out",
      scrollTrigger: {
        trigger: formRef.current,
        start: "top 80%",
        toggleActions: "play none none reverse"
      }
    }
  );
};